"use client";
import { useFormContext, useWatch } from "react-hook-form";
import { Card, IconButton, Typography } from "@mui/material";
import { SelectField } from "../Fields/SelectField";
import { Button } from "../Button";
import { WebShopIcon } from "../WebShopIcon";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import isEmpty from "lodash/isEmpty";
import { CrossProductType, CrossType } from "./types";
import { BaseHintProps } from "../HintCard/lib/type";
import { useHintCardStore } from "@/src/stores/useHintCardStore";
import DOMPurify from "dompurify";
import { parseLinks } from "../HintCard/components/HintCard";
import { useState } from "react";
import { useCrossHint } from ".";

export type CrossCardOption = { label: string; value: string; id: string };

type CrossProductCardProps = {
  title?: string;
  description?: string;
  details?: string;
  options?: Array<CrossCardOption>;
  id?: string;
  multiselect?: boolean;
  type?: CrossProductType;
  hints?: Omit<BaseHintProps, "variant">[];
};

export const CrossCard = ({
  title = "",
  description = "",
  details = "",
  options = [],
  id = "",
  multiselect = false,
  type,
  hints,
}: CrossProductCardProps) => {
  const { control, setValue, watch } = useFormContext();
  const { setHintsList } = useHintCardStore();

  const { hintedCrossId, selectHintedCross } = useCrossHint();

  const selectedCards = (useWatch({
    name: "selectedCards",
    control,
  }) || []) as { id: string; option: CrossCardOption }[];

  const selectedCard = selectedCards.find((card) => card.id === id);
  const isSelected = !!selectedCard;
  const cardIndex = selectedCards.findIndex((card) => card.id === id);
  const selectFieldName = `selectedCards.${cardIndex}.option`;

  const isHintShown = hintedCrossId === id;

  const handleCrossCardRemove = () => {
    return selectedCards.filter((card) => card.id !== id);
  };
  const handleCrossCardSelect = () => {
    return [{ id, option: options[0] || "", type }];
  };
  const handleMultipleCrossCardSelect = () => {
    return [...selectedCards, { id, option: options[0] || "", type }];
  };

  const handleCardClick = () => {
    const updatedSelectedCards = isSelected
      ? handleCrossCardRemove()
      : multiselect
        ? handleMultipleCrossCardSelect()
        : handleCrossCardSelect();
    setHintsList(hints || []);
    setValue("selectedCards", updatedSelectedCards);
  };
  const sanitizedDescription = parseLinks(
    DOMPurify.sanitize(hints?.[0]?.contentDescription || ""),
  );
  return (
    <Card
      className={cn(
        "rounded-lg border border-solid border-light-blue bg-white p-4 shadow-none",
        isSelected && "border-blue bg-super-light-blue",
      )}
    >
      <div className="flex h-full flex-1 flex-col justify-between gap-4">
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Typography className="text-base font-semibold text-dark-blue">
                {title}
              </Typography>
              {!!sanitizedDescription && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    selectHintedCross(id);
                  }}
                  className={cn("px-2", {
                    "md:hidden": true,
                  })}
                >
                  <WebShopIcon
                      name={"questionCircle"}
                      className={cn("group-hover:fill-initial", {
                        "border": isSelected,
                        "border-blue": isSelected && isHintShown,
                      })}
                      classNameCircle={cn("group-hover:fill-initial", {
                        "fill-white": isSelected,
                        "fill-[#EEF7FC]": !isSelected && isHintShown,
                      })}
                      classNamePath={cn("group-hover:fill-initial", {
                        "fill-dark-gray": isSelected,
                        "fill-blue": isHintShown
                      })}
                  />
                </IconButton>
              )}
            </div>
            {!!sanitizedDescription && isHintShown && (
              <div
                className={cn("rounded-sm p-3 text-[15px] leading-[22px] text-dark-gray", {
                  "bg-white border border-solid border-light-blue": isSelected,
                  "bg-[#009DDE0F]": !isSelected,
                })}
                dangerouslySetInnerHTML={{
                  __html: sanitizedDescription,
                }}
              ></div>
            )}
          </div>
          <Typography className="text-xs text-dark-gray">
            {description}
          </Typography>
          <Typography className="text-base font-semibold text-dark-gray">
            {details}
          </Typography>
        </div>
        {isSelected && !isEmpty(options) && (
          <SelectField
            name={selectFieldName}
            control={control}
            options={options}
            label="Страхова сума"
          />
        )}
        <Button
          onClick={handleCardClick}
          variant="outlined"
          className="bg-white"
        >
          {isSelected ? (
            <div className="flex items-center gap-2">
              <WebShopIcon name="check" width={16} height={16} />
              Обрано
            </div>
          ) : (
            <div>Обрати</div>
          )}
        </Button>
      </div>
    </Card>
  );
};
